import { useMemo } from 'react'

import { useQuery } from '@apollo/client'

import { GET_CARRIER_BY_DOT_NUMBER } from 'src/graphql/carriers.graphql'

import { KnownContact, reachableContacts } from './knownContacts'

export interface KnownCarrierContacts {
  /** Reachable contacts only, in the directory's order (preferred, then most-seen). */
  contacts: KnownContact[]
  count: number
  loading: boolean
  carrierName: string | null
  /** The directory row for the DOT; null when there is none. */
  carrierId: string | null
  faxNumber: string | null
}

/**
 * The contacts the directory holds for a DOT number, as the recruiter screens
 * count them. Same query and variables as ContactAutocomplete's own lookup, so
 * the Apollo cache serves both from one request.
 *
 * No re-sorting: the first contact is the one the directory ranks first, and
 * that is the one "Use" and the carrier-pick prefill take.
 */
export const useKnownCarrierContacts = (
  dotNumber?: string | null
): KnownCarrierContacts => {
  const dot = dotNumber?.trim() || ''
  const { data, loading } = useQuery(GET_CARRIER_BY_DOT_NUMBER, {
    variables: { dotNumber: dot },
    skip: !dot,
  })

  const carrier = data?.carrierByDotNumber
  const contacts = useMemo(
    () => reachableContacts<KnownContact>(carrier?.contacts),
    [carrier]
  )

  return {
    contacts,
    count: contacts.length,
    loading: Boolean(dot) && loading,
    carrierName: carrier?.dbaName || carrier?.legalName || null,
    carrierId: carrier?.carrierId ?? null,
    faxNumber: carrier?.faxNumber || null,
  }
}
